/**
 * Theme preference reader/writer. The active theme is the `data-theme` attribute
 * on `<html>` (the design tokens key their dark/dim overrides off it). Two themes
 * ship in v1: `light` (default) and `dark`. The preference persists in
 * localStorage under `hostingportal.theme`; index.html applies it before first paint to
 * avoid a flash.
 */

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'hostingportal.theme'

export function getTheme(): Theme {
  if (typeof document === 'undefined') return 'light'
  const attr = document.documentElement.getAttribute('data-theme')
  return attr === 'dark' ? 'dark' : 'light'
}

export function setTheme(theme: Theme): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme)
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    /* storage may be unavailable (private mode) - non-fatal */
  }
}

export function toggleTheme(): Theme {
  const next: Theme = getTheme() === 'dark' ? 'light' : 'dark'
  setTheme(next)
  return next
}

/** Apply the persisted theme (or the light default) to <html>. Idempotent. */
export function initTheme(): Theme {
  let stored: Theme = 'light'
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'dark' || raw === 'light') stored = raw
  } catch {
    /* ignore */
  }
  setTheme(stored)
  return stored
}
